import React, { useMemo } from "react";
import "./ParticleLoader.css";

const PARTICLE_COUNT = 62;
const LAP_DURATION_SEC = 3;

/**
 * Full-viewport / section particle ring loader themed with --accent-color and --bg-primary.
 *
 * @param {object} props
 * @param {string} [props.message] — Optional caption below the animation
 * @param {boolean} [props.fillViewport] — min-height 100vh (e.g. route / Suspense fallbacks)
 * @param {boolean} [props.embedded] — Transparent background, tighter padding (cards, tables)
 * @param {boolean} [props.compact] — Smaller orbit and particles
 * @param {boolean} [props.contentBackground] — Use --bg-gradient like admin .content
 * @param {string} [props.className] — Extra classes on the root
 */
function ParticleLoader({
  message,
  fillViewport = false,
  embedded = false,
  compact = false,
  contentBackground = false,
  className = "",
}) {
  const dots = useMemo(() => {
    const out = [];
    for (let i = 1; i <= PARTICLE_COUNT; i += 1) {
      const angle = (i / PARTICLE_COUNT) * 720;
      const delay = i * (LAP_DURATION_SEC / PARTICLE_COUNT);
      out.push(
        <span
          key={i}
          className="particle-loader__dot"
          style={{
            "--angle": `${angle}deg`,
            "--delay": `${delay}s`,
          }}
          aria-hidden
        />,
      );
    }
    return out;
  }, []);

  const rootClass = [
    "particle-loader",
    fillViewport && "particle-loader--fill-viewport",
    embedded && "particle-loader--embedded",
    compact && "particle-loader--compact",
    contentBackground && "particle-loader--content-bg",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={message || "Loading"}
    >
      <div className="particle-loader__stage">
        <div className="particle-loader__orbit">
          <div className="particle-loader__wrapper">{dots}</div>
        </div>
        {message ? <p className="particle-loader__message">{message}</p> : null}
      </div>
    </div>
  );
}

export default ParticleLoader;
